/**
 * Partial Integration Logs Component
 * This component handles the display and management of partial integration logs.
 */
import {
	Component,
	ComponentFactoryResolver,
	ElementRef,
	EventEmitter,
	OnInit,
	Output,
	ViewChild
} from "@angular/core";
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
// Services
import { GridBaseComponent, SubContentService } from "@cv-invision/core";
import { PartialIntegrationService } from "./services/data-loader-partial-integration.service";
// Models
import { ORIGIN, URLS } from "./models/partial-integration.model";
import {
	COLUMNS_RELIANCEFIRST,
	COLUMNS_MIMUTUAL,
	FILTER_RELIANCEFIRST,
	FILTER_MIMUTUAL
} from "./models/integrations";
// Components
import { CustomActionPartialOrdersCellComponent } from "./components/custom-action-grid-cell/custom-action-grid-cell.component";
import { ShowPartialModalComponent } from "./components/modal/show-partial-modal.component";
import { LogsPartialDescriptionComponent } from "./components/logs-partial-description/logs-partial-description.component";
// Others
import { GridOptions, RowNode } from "ag-grid-community";
import { CvDateFormatter } from "@cv-invision/cv.component-library.ux";
import { OriginSourceData } from "../service/nexsys-monitor.service";

@Component({
	selector: 'cv-invision-partial-integration-logs',
	templateUrl: './partial-integration.component.html',
	styleUrls: ['./partial-integration.component.scss']
})
export class PartialIntegrationComponent extends GridBaseComponent implements OnInit {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	@Output() toggleSubContentPanel: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('filterForm') filterForm: ElementRef;

	protected expandedRow: RowNode;
	protected willCollapseRow: RowNode;
	protected INCREMENT_GRID_HEIGHT_ON_EXPAND = 212;
	gridOptions: GridOptions;
	origin: ORIGIN;
	orderId: string;
	filterLogs: any = {};

	constructor(
		private router: Router,
		private subContentService: SubContentService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private partialIntegrationService: PartialIntegrationService,
		private dateService: CvDateFormatter,
	) {
		super();
	}

	ngOnInit(): void {
		// Determine the origin based on the current URL
		switch (true) {
			case this.router.url.includes(URLS.RELIANCE_FIRST):
				this.origin = ORIGIN.RELIANCE_FIRST;
				this.gridOptions.columnDefs = new COLUMNS_RELIANCEFIRST(this.dateService).columns;
				this.filterLogs = new FILTER_RELIANCEFIRST();
				break;
			case this.router.url.includes(URLS.MI_MUTUAL):
				this.origin = ORIGIN.MI_MUTUAL;
				this.gridOptions.columnDefs = new COLUMNS_MIMUTUAL(this.dateService).columns;
				this.filterLogs = new FILTER_MIMUTUAL();
				break;
		}

		this.gridOptions.frameworkComponents = {
			CustomActionPartialOrdersCellComponent: CustomActionPartialOrdersCellComponent,
			ShowPartialModalComponent: ShowPartialModalComponent
		};
		this.gridOptions.context = {parentComponent: this};

		// Load grid data upon component initialization
		this.loadGridData();

		this.partialIntegrationService
			.getReloadListListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: OriginSourceData) => {
				if (data.origin === 'orders') {
					this.loadGridData();
				}
			});
	}

	/**
	 * Expand Row node
	 * @param row
	 */
	expandRow(row: RowNode): void {
		if (this.willCollapseRow && this.willCollapseRow.rowIndex === row.rowIndex) {
			this.willCollapseRow = null;
		}
		if (!this.expandedRow || this.expandedRow.rowIndex !== row.rowIndex) {
			this.expandedRow = row;
			this.gridApi.resetRowHeights();

			if (this.gridData.length === 1) {
				this.gridHeight = 52 + this.getGridHeight(this.gridData.length);
			} else {
				this.gridHeight = this.gridHeight + this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
			}
		}
	}

	/**
	 * Returns the current expanded row.
	 */
	getExpandedRow(): RowNode {
		return this.expandedRow;
	}

	/**
	 * Collapse the Row node
	 * @param row
	 */
	collapseRow(row: RowNode): void {
		if (this.expandedRow && row.rowIndex === this.expandedRow.rowIndex) {
			this.expandedRow = null;
			this.gridApi.resetRowHeights();
		}
	}

	/**
	 * This method was created to avoid the collision of the open/close events of a select list
	 * from a different column but the same row.
	 * Collapse the Row node
	 * @param row
	 */
	collapseRowWithDelay(row: RowNode): void {
		this.willCollapseRow = row;
		setTimeout(() => {
			if (this.willCollapseRow !== null) {
				this.collapseRow(this.willCollapseRow);
				this.willCollapseRow = null;
				this.gridHeight = this.gridHeight - this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
			}
		}, 200);
	}

	/**
	 * Sets the row height.
	 * @param params
	 */
	getRowHeight = (params: any): number => {
		const rowNode: RowNode = params.node;
		if (this.expandedRow && rowNode.rowIndex === this.expandedRow.rowIndex) {
			return 125;
		} else {
			if (this.gridData.length === 1) {
				return 62;
			} else {
				return this.DEFAULT_ROW_HEIGHT
			}
		}
	}

	/**
	 * Load grid data from the MI Mutual service.
	 */
	loadGridData(): void {
		this.partialIntegrationService.getData(this.origin, this.filterLogs).subscribe(
			(res) => {
				this.handleData(res);
			},
			error => console.error(error)
		);
	}

	/**
     * Handle the response data and update the grid.
     * @param res The response data from the service.
     */
	private handleData(res) {
		this.gridData = res;
		this.gridPagination.pagination.limit = res.length;
		this.gridPagination.pagination.totalCount = res.length;
		this.gridHeight = this.getGridHeight(res && res.length > 0 ? res.length : 0);
	}

	/**
	 * Handle changes in filter inputs.
	 * @param filterName The name of the filter being changed.
	 * @param input The input value of the filter.
	 */
	onFiltersChange(filterName: string, input: any): void {
		switch (filterName) {
			case 'limit':
				this.filterLogs.limit = input.value;
				break;
			default:
				this.filterLogs[filterName] = input.value;
				break;
		}

		this.loadGridData();
	}

	/**
	 * Clear all applied filters and reload grid data.
	 */
	clearAllFilters(): void {
		switch (true) {
			case this.router.url.includes(URLS.RELIANCE_FIRST):
				this.filterLogs = new FILTER_RELIANCEFIRST();
				break;
			case this.router.url.includes(URLS.MI_MUTUAL):
				this.filterLogs = new FILTER_MIMUTUAL();
				break;
		}

		// Clear input fields
		if (this.filterForm) {
            this.filterForm.nativeElement.reset();
        }

		this.loadGridData();
	}

	/**
	 * Handle the selection of a row in the grid.
	 * Opens a sub-component with details of the selected row.
	 */
	public onSelectionChanged(): void {
		const selectedRows = this.gridApi.getSelectedRows();
		const selectedRow = selectedRows[0];
		if (selectedRow) {
			this.openSubComponent(selectedRow);
		} else {
			this.subContentService.close();
		}
	}

	/**
	 * Open a sub-component with details of the selected row.
	 * @param selectedRow The selected row to display details for.
	 */
	private openSubComponent(selectedRow: any = null): void {
		// Open logs detail sub-component
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: LogsPartialDescriptionComponent,
				data: { ...selectedRow, source: this.origin }
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}
}
